import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { OtpCodeForm } from '@/components/auth/OtpCodeForm'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { getPasswordConfirmationState } from '@/lib/passwordConfirmation'
import { cn } from '@/lib/utils'

const inputClassName =
    'appearance-none rounded-md block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'

type Step = 'code' | 'password' | 'success'

const PageShell = ({ title, subtitle, children }: { title: string; subtitle: React.ReactNode; children: React.ReactNode }) => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full space-y-8">
            <div className="text-center">
                <h2 className="text-3xl font-extrabold text-gray-900">{title}</h2>
                <p className="mt-2 text-sm text-gray-600">{subtitle}</p>
            </div>
            {children}
        </div>
    </div>
)

export const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams()
    const email = searchParams.get('email') ?? ''
    const [step, setStep] = useState<Step>('code')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const navigate = useNavigate()
    const { resetPassword } = useAuth()

    useEffect(() => {
        // Emails built from the former link template still land here with the
        // recovery token in the URL; Supabase then emits PASSWORD_RECOVERY.
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
            if (event === 'PASSWORD_RECOVERY') {
                setStep('password')
            }
        })

        return () => subscription.unsubscribe()
    }, [])

    // Without an email to verify a code against, only a former recovery link can
    // move this page forward; give it 4s, then send back to the request page.
    useEffect(() => {
        if (email || step !== 'code') return
        const timer = setTimeout(() => navigate('/reset-password-request'), 4000)
        return () => clearTimeout(timer)
    }, [email, step, navigate])

    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        if (password.length < 6) {
            setError('Le mot de passe doit contenir au moins 6 caractères')
            return
        }
        if (password !== confirmPassword) {
            setError('Les mots de passe ne correspondent pas')
            return
        }

        setIsLoading(true)
        try {
            const { error } = await supabase.auth.updateUser({ password })
            if (error) {
                setError(error.message)
            } else {
                setStep('success')
                setTimeout(() => navigate('/login'), 2000)
            }
        } catch {
            setError('Une erreur est survenue. Veuillez réessayer.')
        } finally {
            setIsLoading(false)
        }
    }

    if (step === 'success') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
                <div className="max-w-md w-full text-center space-y-4">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                        <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-900">Mot de passe mis à jour !</h2>
                    <p className="text-sm text-gray-600">Redirection vers la connexion...</p>
                </div>
            </div>
        )
    }

    if (step === 'password') {
        const { isMismatchVisible, isConfirmed } = getPasswordConfirmationState(password, confirmPassword)

        return (
            <PageShell title="Nouveau mot de passe" subtitle="Entrez votre nouveau mot de passe ci-dessous">
                <form className="mt-8 space-y-6" onSubmit={handleUpdatePassword}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                Nouveau mot de passe
                            </label>
                            <PasswordInput
                                id="password"
                                autoComplete="new-password"
                                required
                                autoFocus
                                wrapperClassName="mt-1"
                                className={inputClassName}
                                placeholder="Au moins 6 caractères"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                Confirmer le mot de passe
                            </label>
                            <PasswordInput
                                id="confirmPassword"
                                autoComplete="new-password"
                                required
                                wrapperClassName="mt-1"
                                className={cn(inputClassName, isMismatchVisible && 'border-red-400 focus:border-red-500 focus:ring-red-500')}
                                placeholder="Confirmez le mot de passe"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                aria-invalid={isMismatchVisible}
                                aria-describedby={isMismatchVisible ? 'password-mismatch' : undefined}
                            />
                            {isMismatchVisible && (
                                <p id="password-mismatch" className="mt-1 text-sm text-red-600">
                                    Les mots de passe ne correspondent pas
                                </p>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                            {error}
                        </div>
                    )}

                    <Button type="submit" disabled={isLoading || !isConfirmed} className="w-full">
                        {isLoading ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
                    </Button>
                </form>
            </PageShell>
        )
    }

    if (!email) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600 text-sm">Vérification du lien...</p>
                </div>
            </div>
        )
    }

    return (
        <PageShell
            title="Saisissez le code"
            subtitle={<>Si un compte existe pour <strong>{email}</strong>, un code de vérification vous a été envoyé par e-mail.</>}
        >
            <OtpCodeForm
                submitLabel="Valider le code"
                onVerify={async (code) => {
                    const result = await supabase.auth.verifyOtp({ email, token: code, type: 'recovery' })
                    if (!result.error) setStep('password')
                    return result
                }}
                onResend={() => resetPassword(email)}
            >
                <Link to="/reset-password-request" className="text-indigo-600 hover:text-indigo-500">
                    Utiliser une autre adresse e-mail
                </Link>
                <Link to="/login" className="text-indigo-600 hover:text-indigo-500">
                    Retour à la connexion
                </Link>
            </OtpCodeForm>
        </PageShell>
    )
}
